import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import useRegistrationDraft, { stripSignatures } from '../hooks/useRegistrationDraft';
import NewSignature from '../utils/NewSignature';
import {InputMask} from '@react-input/mask';
import '../style/PatientRegistration.css';
import jsPDF from 'jspdf';
import InsuranceCardScanner from "./customComponents/InsuranceCardScanner.jsx"
import DriversLicenseScanner from "./customComponents/DriversLicenseScanner.jsx"

// import NavBar from '../Tools/NavBar';
import { useContext } from 'react';
import { GlobalStateContext } from '../utils/GlobalStateContext';
import {getUserId, uploadToBucket, formatPhoneNumber, unformatPhoneNumber, formatToLocalDate, formatDateForAPI} from "../utils/utils";


// Stable client-side id for an insurance block. Used as the React list key and as
// the storage key for per-block scanner/confidence state, so that removing a block
// can never misbind a scanned card image to a different insurance record.
let _insuranceUidCounter = 0;
const genInsuranceUid = () => `ins_${Date.now().toString(36)}_${(_insuranceUidCounter++).toString(36)}`;

const generateBillingPDF = async ({
  patients_id,
  insuranceData,
  patientDemographics,
  insuredSignature,
  patientSignature,
  type,
  user
}) => {
  const logoImagePath = "/img/health_guardian.jpeg"; // Adjust if needed

  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (error) => reject(new Error("Failed to load image"));
      img.src = src;
    });
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  try {
    // const {currentUser} = useContext(GlobalStateContext);
    // const user = getUserId(currentUser);
    const doc = new jsPDF();
    let yPosition = 10;
    const leftMargin = 10;
    const contentWidth = 180;
    const maxYPosition = 270;
    const baseLineHeight = 8;
    const elementSpacing = 4;
    const lightSpacing = 2;

    const checkPageOverflow = (linesCount = 1) => {
      const totalHeight = linesCount * baseLineHeight + elementSpacing;
      if (yPosition + totalHeight > maxYPosition) {
        doc.addPage();
        yPosition = 10;
      }
    };

    const drawHeader = (text) => {
      checkPageOverflow();
      doc.setFillColor("#87CEFA");
      doc.rect(leftMargin, yPosition, contentWidth, baseLineHeight + 2, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFont("helvetica", "bold");
      doc.text(text, leftMargin + 2, yPosition + baseLineHeight);
      yPosition += baseLineHeight + elementSpacing;
      doc.setTextColor("#000000");
      yPosition += elementSpacing;
    };

    const addStyledField = (label, value, x = leftMargin + 10) => {
      const lines = doc.splitTextToSize(value || "N/A", contentWidth - 30);
      checkPageOverflow(lines.length);
      doc.setTextColor("#808080");
      doc.text(`${label}:`, x, yPosition);
      const labelWidth = doc.getTextWidth(`${label}: `);
      doc.setTextColor("#000000");
      doc.text(lines, x + labelWidth + 1, yPosition);
      yPosition += lines.length * elementSpacing + baseLineHeight;
    };

    const addWrappedText = (text, x = leftMargin) => {
      const lines = doc.splitTextToSize(text || "N/A", contentWidth);
      checkPageOverflow(lines.length);
      doc.setTextColor("#000000");
      doc.setFont("helvetica", "normal");
      doc.text(lines, x + leftMargin , yPosition);
      yPosition += lines.length * baseLineHeight + elementSpacing;
    };

    // Title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor("#000000");
    doc.text("Billing Authorization", leftMargin, yPosition);

    // Logo
    const logoWidth = 20;
    const logoHeight = 20;
    const xPositionLogo = doc.internal.pageSize.getWidth() - leftMargin - logoWidth;
    const yPositionLogo = 0;

    try {
      const imgData = await loadImage(logoImagePath);
      doc.addImage(imgData, "JPEG", xPositionLogo, yPositionLogo, logoWidth, logoHeight, undefined, "FAST");
    } catch {
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.text("Health Guardian Imaging Logo", xPositionLogo, yPositionLogo);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
    }

    yPosition += baseLineHeight + lightSpacing;
    doc.setLineWidth(0.5);
    doc.line(leftMargin, yPosition, doc.internal.pageSize.getWidth() - leftMargin, yPosition);
    yPosition += baseLineHeight + lightSpacing;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    // Patient Info
    drawHeader("Patient Information");
    addStyledField("Name", `${patientDemographics.first_name} ${patientDemographics.last_name}`);
    addStyledField("Date of Birth", patientDemographics.date_of_birth);
    addStyledField("Phone", patientDemographics.primary_phone);

    // Insurance Info
    insuranceData.forEach((insurance, index) => {
      drawHeader(`Insurance ${index + 1}`);
      addStyledField("Insurance Name", insurance.insurance_name);
      addStyledField("Address", `${insurance.insurance_address}, ${insurance.insurance_city}, ${insurance.insurance_state} ${insurance.insurance_zip}`);
      addStyledField("Phone", insurance.insurance_phone);
      addStyledField("Member ID", insurance.member_id);
      addStyledField("Group Number", insurance.group_number);
      addStyledField("Effective Date", insurance.effective_date);
      addStyledField("Termination Date", insurance.termination_date);
      addStyledField("Subscriber Name", insurance.subscriber_name);
      addStyledField("Subscriber DOB", insurance.subscriber_dob);
      addStyledField("Relationship", insurance.relationship);
    });

    // Authorization Text
    drawHeader("Authorization");
    addWrappedText("I hereby authorize the release of medical information necessary to process this claim and request payment of benefits to the provider. I understand that I am financially responsible for any balance not covered by my insurance.");

    // Signature Section
    drawHeader("Signatures");

    const addSignatureBlock = async (label, signature) => {
      if (!signature?.url) {
        addWrappedText(`${label} signature not provided.`);
        return;
      }

      const signedAt = signature.timestamp ? formatDateTime(signature.timestamp) : "N/A";
      addWrappedText(`Signed electronically by ${label} on ${signedAt}:`);

      try {
        const imgProps = doc.getImageProperties(signature.url);
        const imgWidth = contentWidth;
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
        checkPageOverflow(Math.ceil(imgHeight / baseLineHeight));
        doc.addImage(signature.url, "PNG", leftMargin, yPosition, imgWidth, imgHeight, undefined, "FAST");
        yPosition += imgHeight + elementSpacing;
      } catch (error) {
        logger.error(`Error adding ${label} signature image:`, error);
        addWrappedText(`${label} signature image could not be loaded.`);
      }
    };

    await addSignatureBlock("Patient", patientSignature);
    await addSignatureBlock("Insured", insuredSignature);

    // Upload PDF
    const pdfBlob = doc.output("blob");
    try {
      await uploadToBucket(pdfBlob, "Billing_Authorization", patients_id || "Unknown PID", `Billing`, type, user, 'RIS_BILLING/'); // Billing/
      alert("Billing PDF uploaded successfully!");
    } catch (error) {
      logger.error("Error uploading billing PDF:", error);
      alert("Failed to upload billing PDF.");
    }
  } catch (error) {
    logger.error("Error generating billing PDF:", error);
    alert("Failed to generate billing PDF.");
  }
};

const generateHipaaPDF = async ({
  patients_id,
  demographics,
  emergencyContacts,
  signatureImage,
  signatureText,
  signatureTimestamp,
  type,
  user
}) => {

  const logoImagePath = "/img/health_guardian.jpeg"; // Adjust if needed
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (error) => reject(new Error("Failed to load image"));
      img.src = src;
    });
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  try {
    
    const doc = new jsPDF();
    let yPosition = 10;
    const leftMargin = 10;
    const contentWidth = 180;
    const maxYPosition = 270;
    const baseLineHeight = 8;
    const elementSpacing = 4;
    const lightSpacing = 2;

    const checkPageOverflow = (linesCount = 1) => {
      const totalHeight = linesCount * baseLineHeight + elementSpacing;
      if (yPosition + totalHeight > maxYPosition) {
        doc.addPage();
        yPosition = 10;
      }
    };

    const drawHeader = (text) => {
      checkPageOverflow();
      doc.setFillColor("#87CEFA");
      doc.rect(leftMargin, yPosition, contentWidth, baseLineHeight + 2, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFont("helvetica", "bold");
      doc.text(text, leftMargin + 2, yPosition + baseLineHeight);
      yPosition += baseLineHeight + elementSpacing;
      doc.setTextColor("#000000");
      yPosition += elementSpacing;
    };

    const addStyledField = (label, value, x = leftMargin + 10) => {
      const lines = doc.splitTextToSize(value || "N/A", contentWidth - 30);
      checkPageOverflow(lines.length);
      doc.setTextColor("#808080");
      doc.text(`${label}:`, x, yPosition);
      const labelWidth = doc.getTextWidth(`${label}: `);
      doc.setTextColor("#000000");
      doc.text(lines, x + labelWidth + 1, yPosition);
      yPosition += lines.length * elementSpacing + baseLineHeight;
    };

    const addWrappedText = (text, x = leftMargin) => {
      const lines = doc.splitTextToSize(text || "N/A", contentWidth);
      checkPageOverflow(lines.length);
      doc.setTextColor("#000000");
      doc.setFont("helvetica", "normal");
      doc.text(lines, x + leftMargin, yPosition);
      yPosition += lines.length * baseLineHeight + elementSpacing;
    };

    // Title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor("#000000");
    doc.text("HIPAA Release Form", leftMargin, yPosition);

    // Logo
    const logoWidth = 20;
    const logoHeight = 20;
    const xPositionLogo = doc.internal.pageSize.getWidth() - leftMargin - logoWidth;
    const yPositionLogo = 0;

    try {
      const imgData = await loadImage(logoImagePath);
      doc.addImage(imgData, "JPEG", xPositionLogo, yPositionLogo, logoWidth, logoHeight, undefined, "FAST");
    } catch {
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.text("Health Guardian Logo", xPositionLogo, yPositionLogo);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
    }

    yPosition += baseLineHeight + lightSpacing;
    doc.setLineWidth(0.5);
    doc.line(leftMargin, yPosition, doc.internal.pageSize.getWidth() - leftMargin, yPosition);
    yPosition += baseLineHeight + lightSpacing;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    // Patient Info
    drawHeader("Patient Information");
    addStyledField("Name", `${demographics.first_name} ${demographics.last_name}`);
    addStyledField("Date of Birth", demographics.date_of_birth);

    // Emergency Contacts
    drawHeader("Authorized Contacts");
    emergencyContacts.forEach((contact, i) => {
      if (contact.name) {
        addStyledField(`Contact ${i + 1}`, `${contact.name} (${contact.relation})`);
      }
    });

    // Disclosure Details
    drawHeader("Information to be Released");
    addWrappedText("• All Medical Records including radiology interpretations\n• Lab Results");

    drawHeader("Purpose of Disclosure");
    addWrappedText("For treatment by another healthcare provider, for insurance purposes, or Emergency contact.");

    drawHeader("Limitations");
    addWrappedText("• This authorization is valid for 2 years from the date signed.\n• I understand that I can revoke this authorization at any time by providing written notice to Health Guardian Imaging.");

    // Signature
    drawHeader("Signature");

    addWrappedText(`Signed electronically by ${signatureText || "N/A"} on ${formatDateTime(signatureTimestamp)}`);

    try {
      const imgProps = doc.getImageProperties(signatureImage);
      const imgWidth = contentWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
      checkPageOverflow(Math.ceil(imgHeight / baseLineHeight));
      doc.addImage(signatureImage, "PNG", leftMargin, yPosition, imgWidth, imgHeight, undefined, "FAST");
      yPosition += imgHeight + elementSpacing;
    } catch (error) {
      logger.error("Error adding signature image:", error);
      addWrappedText("Signature image could not be loaded.");
    }

    // Upload PDF
    const pdfBlob = doc.output("blob");
    try {
      
      await uploadToBucket(pdfBlob, "HIPAA_Release", patients_id || "Unknown PID", `HIPAA_Form`, type, user, "RIS_HIPAA/"); // HIPAA/
      alert("HIPAA PDF uploaded successfully!");
    } catch (error) {
      logger.error("Error uploading HIPAA PDF:", error);
      alert("Failed to upload HIPAA PDF.");
    }
  } catch (error) {
    logger.error("Error generating HIPAA PDF:", error);
    alert("Failed to generate HIPAA PDF.");
  }
};

// Demographics regex completed.
const Demographics = ({ data, setData, navigation, isMinor }) => {
  const [fieldConfidences, setFieldConfidences] = useState({});

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
    // Clear confidence indicator when staff manually edits a scanned field
    if (fieldConfidences[e.target.name]) {
      setFieldConfidences(prev => ({
        ...prev,
        [e.target.name]: { ...prev[e.target.name], confidence: 100 },
      }));
    }
  };

  const handleDLFieldsExtracted = useCallback((fields, fieldDetails) => {
    const updated = { ...data };
    for (const [key, value] of Object.entries(fields)) {
      if (value && fieldDetails[key]?.confidence > 0) {
        updated[key] = value;
      }
    }
    setData(updated);
    setFieldConfidences(fieldDetails);
  }, [data, setData]);

  const getFieldStyle = (fieldName) => {
    const confidence = fieldConfidences[fieldName]?.confidence;
    if (confidence === undefined || confidence === 0 || confidence >= 80) return {};
    if (confidence >= 40) return { borderColor: '#eab308', borderWidth: 2, backgroundColor: '#fffbeb' };
    return { borderColor: '#ef4444', borderWidth: 2, backgroundColor: '#fef2f2' };
  };

  const getConfidenceBadge = (fieldName) => {
    const confidence = fieldConfidences[fieldName]?.confidence;
    if (confidence === undefined || confidence === 0 || confidence >= 80) return null;
    const isLow = confidence < 40;
    return (
      <span style={{
        fontSize: 11, padding: '1px 6px', borderRadius: 10, marginLeft: 6,
        backgroundColor: isLow ? '#fef2f2' : '#fffbeb',
        color: isLow ? '#991b1b' : '#92400e',
        fontWeight: 500,
      }}>
        {isLow ? 'Low confidence' : 'Please verify'}
      </span>
    );
  };

  return (
    <div className="chk-form-section">
      <h2 className="chk-section-heading">1. Demographics</h2>
      {navigation}

      {/* Driver's License Scanner */}
      <DriversLicenseScanner onFieldsExtracted={handleDLFieldsExtracted} />

      <div className="chk-form-grid">
        <span className="chk-form-group-header" style={{gridColumn: '1 / -1'}}>Personal Information</span>
        <h4 className="chk-form-grid-title">First Name: <span className="required">*</span> {getConfidenceBadge('first_name')}</h4>
        <input name="first_name" value={data.first_name || ''} onChange={(e) => /^[A-Za-z' -'-]*$/.test(e.target.value) && handleChange(e)} placeholder="First Name" className="chk-input" style={getFieldStyle('first_name')} required />
        <h4 className="chk-form-grid-title">Middle Name: {getConfidenceBadge('middle_name')}</h4>
        <input name="middle_name" value={data.middle_name || ''} onChange={(e) => /^[A-Za-z -'-]*$/.test(e.target.value) && handleChange(e)} placeholder="Middle Name" className="chk-input" style={getFieldStyle('middle_name')} />
        <h4 className="chk-form-grid-title">Last Name: <span className="required">*</span> {getConfidenceBadge('last_name')}</h4>
        <input name="last_name" value={data.last_name || ''} onChange={(e) => /^[A-Za-z' -'-]*$/.test(e.target.value) && handleChange(e)} placeholder="Last Name" className="chk-input" style={getFieldStyle('last_name')} required />
        <h4 className="chk-form-grid-title">Sex: <span className="required">*</span> {getConfidenceBadge('sex')}</h4>
        <select
          name="sex"
          value={data.sex || ''}
          onChange={(e) => handleChange(e)}
          className="chk-input"
          style={getFieldStyle('sex')}
          required
        >
          <option value="">Select Sex</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
        <span className="chk-form-group-header" style={{gridColumn: '1 / -1'}}>Address</span>
        <h4 className="chk-form-grid-title">Street Number: <span className="required">*</span> {getConfidenceBadge('street_number')}</h4>
        <input name="street_number" value={data.street_number || ''} onChange={(e) => /^\d{0,10}$/.test(e.target.value) && handleChange(e)} placeholder="Street Number" className="chk-input" style={getFieldStyle('street_number')} required />
        <h4 className="chk-form-grid-title">Street Name: <span className="required">*</span> {getConfidenceBadge('street_name')}</h4>
        <input name="street_name" value={data.street_name || ''} onChange={(e) => /^[A-Za-z0-9\s]*$/.test(e.target.value) && handleChange(e)} placeholder="Street Name" className="chk-input" style={getFieldStyle('street_name')} required />
        <h4 className="chk-form-grid-title">Apt Number (optional): {getConfidenceBadge('apt_number')}</h4>
        <input name="apt_number" value={data.apt_number || ''} onChange={(e) => handleChange(e)} placeholder="Apt Number (optional)" className="chk-input" style={getFieldStyle('apt_number')} />
        <h4 className="chk-form-grid-title">City: <span className="required">*</span> {getConfidenceBadge('city')}</h4>
        <input name="city" value={data.city || ''} onChange={(e) => /^[A-Za-z\s]*$/.test(e.target.value) && handleChange(e)} placeholder="City" className="chk-input" style={getFieldStyle('city')} required />
        <h4 className="chk-form-grid-title">State: <span className="required">*</span> {getConfidenceBadge('state')}</h4>
        <input name="state" value={data.state || ''} onChange={(e) => /^[A-Za-z]*$/.test(e.target.value) && handleChange(e)} placeholder="State" className="chk-input" style={getFieldStyle('state')} required />
        <h4 className="chk-form-grid-title">Zip Code: <span className="required">*</span> {getConfidenceBadge('zip_code')}</h4>
        <input name="zip_code" value={data.zip_code || ''} onChange={(e) => /^[0-9]{0,5}(-[0-9]{0,4})?$/.test(e.target.value) && handleChange(e)} placeholder="Zip Code" className="chk-input" style={getFieldStyle('zip_code')} required />
        <span className="chk-form-group-header" style={{gridColumn: '1 / -1'}}>Contact Information</span>
        <h4 className="chk-form-grid-title">Primary Phone Number: <span className="required">*</span></h4>
        <InputMask mask="(XXX) XXX-XXXX" replacement={{ X: /\d/ }} name="primary_phone" value={data.primary_phone || ''} onChange={(e) => { const value = e.target.value; if (value === '' || /^[0-9()\s-]+$/.test(value)) handleChange(e); }} placeholder="Primary Phone" className="chk-input" required />
        <h4 className="chk-form-grid-title">Cell Phone:</h4>
        <InputMask mask="(XXX) XXX-XXXX" replacement={{ X: /\d/ }} name="cell_phone" value={data.cell_phone || ''} onChange={(e) => { const value = e.target.value; if (value === '' || /^[0-9()\s-]+$/.test(value)) handleChange(e); }} placeholder="Cell Phone" className="chk-input" />
        <h4 className="chk-form-grid-title">Email: <span className="required">*</span></h4>
        <input name="email" type="email" value={data.email || ''} onChange={(e) => handleChange(e)} onBlur={(e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value) || alert("Invalid email format")} placeholder="Email Address" className="chk-input" required />
        <h4 className="chk-form-grid-title">Date of Birth: <span className="required">*</span> {getConfidenceBadge('date_of_birth')}</h4>
        <input name="date_of_birth" type="date" value={data.date_of_birth || ''} onChange={handleChange} placeholder="Date of Birth" className="chk-input" style={getFieldStyle('date_of_birth')} required />
        <span className="chk-form-group-header" style={{gridColumn: '1 / -1'}}>Additional</span>
        <h4 className="chk-form-grid-title">SSN: </h4>
        <InputMask name="ssn" mask="XXX-XX-XXXX" replacement={{ X: /\d/ }} value={data.ssn || ''} onChange={(e) => handleChange(e)} placeholder="Social Security Number (XXX-XX-XXXX)" className="chk-input" />
        <h4 className="chk-form-grid-title">Parent/Guardian Name:</h4>
        <input name="guardian_name" value={data.guardian_name || ''} onChange={(e) => /^[A-Za-z' ]*$/.test(e.target.value) && handleChange(e)} placeholder="Parent or Guardian Name" className="chk-input" />
      </div>
    </div>
  );
};

const Billing = ({ data, setData, signatures, setSignatures, navigation, demographics, selfPay, setSelfPay, risPatientId, cardScans, persistCardScan, onArchiveInsurance }) => {
    const [insuredSignature, setInsuredSignature] = useState(signatures.insured_signature_image || '');
    const [patientSignature, setPatientSignature] = useState(signatures.patient_signature_image || '');
    const [fieldConfidences, setFieldConfidences] = useState({});

    // Functional update: the scanner fires this immediately before handleCardImages,
    // so both must compose off the latest state rather than a captured `data` snapshot —
    // otherwise the second setData clobbers the first and the extracted fields vanish.
    // Keyed by the insurance block's stable uid (not array index) so a removed block
    // can never misbind extracted fields/confidence to a different insurance record.
    const handleFieldsExtracted = useCallback((uid, fields, fieldDetails) => {
        setData(prev => {
            const i = prev.findIndex(ins => ins.uid === uid);
            if (i === -1) return prev;
            const newInsurances = [...prev];
            const updated = { ...newInsurances[i] };
            for (const [key, value] of Object.entries(fields)) {
                if (value && fieldDetails[key]?.confidence > 0) {
                    updated[key] = value;
                }
            }
            newInsurances[i] = updated;
            return newInsurances;
        });
        setFieldConfidences(prev => ({ ...prev, [uid]: fieldDetails }));
    }, [setData]);

    // Records the GCS object paths of the scanned card images on the insurance
    // block so they can be persisted (insurance_card_images) at registration submit.
    const handleCardImages = useCallback((uid, images) => {
        if (!images || !images.length) return;
        setData(prev => {
            const i = prev.findIndex(ins => ins.uid === uid);
            if (i === -1) return prev;
            const newInsurances = [...prev];
            const existing = newInsurances[i]?.card_images || [];
            newInsurances[i] = { ...newInsurances[i], card_images: [...existing, ...images] };
            return newInsurances;
        });
    }, [setData]);

    const getFieldStyle = (index, fieldName) => {
        const confidence = fieldConfidences[data[index]?.uid]?.[fieldName]?.confidence;
        if (confidence === undefined || confidence === 0 || confidence >= 80) return {};
        if (confidence >= 40) return { borderColor: '#eab308', borderWidth: 2, backgroundColor: '#fffbeb' };
        return { borderColor: '#ef4444', borderWidth: 2, backgroundColor: '#fef2f2' };
    };

    const getConfidenceBadge = (index, fieldName) => {
        const confidence = fieldConfidences[data[index]?.uid]?.[fieldName]?.confidence;
        if (confidence === undefined || confidence === 0 || confidence >= 80) return null;
        const isLow = confidence < 40;
        return (
            <span style={{
                fontSize: 11, padding: '1px 6px', borderRadius: 10, marginLeft: 6,
                backgroundColor: isLow ? '#fef2f2' : '#fffbeb',
                color: isLow ? '#991b1b' : '#92400e',
                fontWeight: 500,
            }}>
                {isLow ? 'Low confidence' : 'Please verify'}
            </span>
        );
    };

    const handleInsuredSignatureSave = (signatureURL) => {
      const timestamp = new Date().toISOString();
      setSignatures((prev) => ({
        ...prev,
        insured_signature_image: signatureURL,
        insured_signature_timestamp: timestamp
      }));
    };

    const handlePatientSignatureSave = (signatureURL) => {
      const timestamp = new Date().toISOString();
      setSignatures((prev) => ({
        ...prev,
        patient_signature_image: signatureURL,
        patient_signature_timestamp: timestamp
      }));
    };

    const handleInsuranceChange = (index, field, value) => {
        const newInsurances = [...data];
        newInsurances[index] = { ...newInsurances[index], [field]: value };
        // When the patient is the subscriber, the payer-required subscriber identity
        // is the patient's own — auto-fill it so staff/patients don't re-type it.
        if (field === 'relationship' && value === 'self') {
            const patientName = `${demographics.first_name || ''} ${demographics.last_name || ''}`.trim();
            newInsurances[index].subscriber_name = patientName;
            newInsurances[index].subscriber_dob = demographics.date_of_birth || '';
        }
        setData(newInsurances);
        // A manual edit makes the field authoritative — bump its confidence to 100.
        // Keyed by the block's stable uid to stay aligned after add/remove.
        const uid = data[index]?.uid;
        if (uid && fieldConfidences[uid]?.[field]) {
            setFieldConfidences(prev => ({
                ...prev,
                [uid]: { ...prev[uid], [field]: { ...prev[uid][field], confidence: 100 } },
            }));
        }
    };

    const addInsurance = () => {
        if (data.length < 3) {
            setData([...data, {
                uid: genInsuranceUid(),
                bi_id: null,
                insurance_name: '',
                insurance_address: '',
                insurance_city: '',
                insurance_state: '',
                insurance_zip: '',
                insurance_phone: '',
                member_id: '',
                group_number: '',
                effective_date: '',
                termination_date: '',
                subscriber_name: '',
                subscriber_dob: '',
                relationship: 'self',
                card_images: []
            }]);
        }
    };

  const removeInsurance = (index) => {
    const removed = data[index];
    // An already-saved policy (has bi_id) is archived on submit, not deleted, to
    // preserve claim/billing history. Newly added (unsaved) blocks just disappear.
    if (removed?.bi_id && onArchiveInsurance) onArchiveInsurance(removed.bi_id);
    setData(data.filter((_, i) => i !== index));
  };

  return (
    <div className="chk-form-section">
      <h2 className="chk-section-heading">2. Billing Information for {demographics.first_name} {demographics.last_name}</h2>
      {navigation}

      {/* Self-Pay Toggle */}
      <div
        className={`chk-self-pay-toggle${selfPay ? ' chk-self-pay-active' : ''}`}
        onClick={() => setSelfPay(!selfPay)}
      >
        <input
          type="checkbox"
          id="self-pay-checkbox"
          checked={selfPay}
          onChange={() => setSelfPay(!selfPay)}
          onClick={(e) => e.stopPropagation()}
          className="chk-self-pay-checkbox"
        />
        <div className="chk-self-pay-text">
          <span className="chk-self-pay-label">Self-Pay</span>
          <span className="chk-self-pay-desc">Patient will pay out-of-pocket. Insurance information is not required.</span>
        </div>
      </div>

      {!selfPay && (
        <>
          {data.map((insurance, index) => (
            <div key={insurance.uid ?? index} className="chk-insurance-card">
              <div className="chk-insurance-card-header">
                <span className="chk-insurance-title">
                  <span className="chk-insurance-badge">#{index + 1}</span>
                  Insurance Information
                </span>
                {data.length > 1 && (
                  <button onClick={() => removeInsurance(index)} className="chk-med-remove-btn">✕ Remove</button>
                )}
              </div>
              <div className="chk-form-insurance-grid">
                <strong><h4 className="chk-form-grid-title">Insurance Name: <span className="required">*</span> {getConfidenceBadge(index, 'insurance_name')}</h4></strong>
                <input name="insurance_name" value={insurance.insurance_name || ''} onChange={(e) => /^[A-Za-z0-9 ]*$/.test(e.target.value) && handleInsuranceChange(index, 'insurance_name', e.target.value)} placeholder="Insurance Name" className="chk-input" style={getFieldStyle(index, 'insurance_name')} />

                <h4 className="chk-form-grid-title">Insurance Address: {getConfidenceBadge(index, 'insurance_address')}</h4>
                <input name="insurance_address" value={insurance.insurance_address || ''} onChange={(e) => handleInsuranceChange(index, 'insurance_address', e.target.value)} placeholder="Insurance Address" className="chk-input" style={getFieldStyle(index, 'insurance_address')} />

                <h4 className="chk-form-grid-title">Insurance City: {getConfidenceBadge(index, 'insurance_city')}</h4>
                <input name="insurance_city" value={insurance.insurance_city || ''} onChange={(e) => handleInsuranceChange(index, 'insurance_city', e.target.value)} placeholder="Insurance City" className="chk-input" style={getFieldStyle(index, 'insurance_city')} />

                <h4 className="chk-form-grid-title">Insurance State: {getConfidenceBadge(index, 'insurance_state')}</h4>
                <input name="insurance_state" value={insurance.insurance_state || ''} onChange={(e) => handleInsuranceChange(index, 'insurance_state', e.target.value)} placeholder="Insurance State" className="chk-input" style={getFieldStyle(index, 'insurance_state')} />

                <h4 className="chk-form-grid-title">Insurance ZIP: {getConfidenceBadge(index, 'insurance_zip')}</h4>
                <InputMask mask="XXXXX" replacement={{ X: /\d/ }} name="insurance_zip" value={insurance.insurance_zip || ''} onChange={(e) => handleInsuranceChange(index, 'insurance_zip', e.target.value)} placeholder="Insurance ZIP" className="chk-input" style={getFieldStyle(index, 'insurance_zip')} />

                <h4 className="chk-form-grid-title">Insurance Phone: {getConfidenceBadge(index, 'insurance_phone')}</h4>
                <InputMask mask="(XXX) XXX-XXXX" replacement={{ X: /\d/ }} name="insurance_phone" value={insurance.insurance_phone || ''} onChange={(e) => { const value = e.target.value; if (value === '' || /^[0-9()\s-]+$/.test(value)) handleInsuranceChange(index, 'insurance_phone', value); }} placeholder="Insurance Phone" className="chk-input" style={getFieldStyle(index, 'insurance_phone')} />

                <strong><h4 className="chk-form-grid-title">Member ID: <span className="required">*</span> {getConfidenceBadge(index, 'member_id')}</h4></strong>
                <input name="member_id" value={insurance.member_id || ''} onChange={(e) => /^[A-Za-z0-9]*$/.test(e.target.value) && handleInsuranceChange(index, 'member_id', e.target.value)} placeholder="Member ID" className="chk-input" style={getFieldStyle(index, 'member_id')} />

                <strong><h4 className="chk-form-grid-title">Group Number: {getConfidenceBadge(index, 'group_number')}</h4></strong>
                <input name="group_number" value={insurance.group_number || ''} onChange={(e) => /^[A-Za-z0-9]*$/.test(e.target.value) && handleInsuranceChange(index, 'group_number', e.target.value)} placeholder="Group Number" className="chk-input" style={getFieldStyle(index, 'group_number')} />

                <strong><h4 className="chk-form-grid-title">Effective Date: {getConfidenceBadge(index, 'effective_date')}</h4></strong>
                <input name="effective_date" type="date" value={insurance.effective_date || ''} onChange={(e) => handleInsuranceChange(index, 'effective_date', e.target.value)} placeholder="Effective Date" className="chk-input" style={getFieldStyle(index, 'effective_date')} />

                <h4 className="chk-form-grid-title">Termination Date: {getConfidenceBadge(index, 'termination_date')}</h4>
                <input name="termination_date" type="date" value={insurance.termination_date || ''} onChange={(e) => handleInsuranceChange(index, 'termination_date', e.target.value)} placeholder="Termination Date" className="chk-input" style={getFieldStyle(index, 'termination_date')} />

                <strong><h4 className="chk-form-grid-title">Subscriber Name: <span className="required">*</span> {getConfidenceBadge(index, 'subscriber_name')}</h4></strong>
                <input name="subscriber_name" value={insurance.subscriber_name || ''} onChange={(e) => handleInsuranceChange(index, 'subscriber_name', e.target.value)} placeholder="Subscriber Name" className="chk-input" style={getFieldStyle(index, 'subscriber_name')} />

                <strong><h4 className="chk-form-grid-title">Subscriber Date of Birth: <span className="required">*</span> {getConfidenceBadge(index, 'subscriber_dob')}</h4></strong>
                <input name="subscriber_dob" type="date" value={insurance.subscriber_dob || ''} onChange={(e) => handleInsuranceChange(index, 'subscriber_dob', e.target.value)} placeholder="Subscriber Date of Birth" className="chk-input" style={getFieldStyle(index, 'subscriber_dob')} />

                <strong><h4 className="chk-form-grid-title">Relationship to Subscriber: <span className="required">*</span> {getConfidenceBadge(index, 'relationship')}</h4></strong>
                <select name="relationship" value={insurance.relationship || ''} onChange={(e) => handleInsuranceChange(index, 'relationship', e.target.value)} className="chk-input" style={getFieldStyle(index, 'relationship')}>
                  <option value="">Select Relationship</option>
                  <option value="self">Self</option>
                  <option value="parent">Parent</option>
                  <option value="guardian">Guardian</option>
                  <option value="spouse">Spouse</option>
                  <option value="child">Child</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Insurance Card Scanner */}
              <InsuranceCardScanner
                index={index}
                uid={insurance.uid}
                risPatientId={risPatientId}
                onFieldsExtracted={handleFieldsExtracted}
                onImagesUploaded={handleCardImages}
                savedImages={cardScans[insurance.uid]?.images}
                savedResult={cardScans[insurance.uid]?.result}
                onPersist={persistCardScan}
              />

            </div>
          ))}
          {data.length < 3 && (
            <button onClick={addInsurance} className="chk-button mt-4">Add Another Insurance</button>
          )}

          <div className="chk-billing-auth">
            <h2>Billing Release</h2>
            <p>I hereby authorize the release of medical information necessary to process this claim and request payment of benefits to the provider. I understand that I am financially responsible for any balance not covered by my insurance.</p>
            <h4>Insured Signature</h4>
            <NewSignature onSignatureSave={handleInsuredSignatureSave} />
            {insuredSignature && (
              <img src={insuredSignature} alt="Insured Signature" className="chk-signature-preview" />
            )}
            <h4>Patient Signature</h4>
            <NewSignature onSignatureSave={handlePatientSignatureSave} />
            {patientSignature && (
              <img src={patientSignature} alt="Patient Signature" className="chk-signature-preview" />
            )}
          </div>
        </>
      )}
    </div>
  );
};

// const ChiefComplaint = ({ data, setData, navigation }) => {
//   const handleChange = (e) => {
//     if (e.target.value.length <= 50) setData(e.target.value);
//   };
//   return (
//     <div className="chk-form-section">
//       <h2 className="text-2xl font-bold mb-4">3. Chief Complaint <span class="required">*</span></h2>
//       {navigation}
//       <p className="mb-2">What are you seeing your health care provider for today? (50 character max)</p>
//       <textarea value={data || ''} onChange={handleChange} placeholder="What brings you here today?" maxLength={50} className="chk-textarea" required />
//       <p className="chk-note">Note: If existing patient and no changes, this may be all you need to fill out.</p>
//     </div>
//   );
// };

const EmergencyContact = ({ data, setData, setShowHipaa, hipaaReviewed, setHipaaReviewed, navigation }) => {
  const handleContactChange = (index, field, value) => {
    const newContacts = [...data];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setData(newContacts);
    if (hipaaReviewed) setHipaaReviewed(false);
  };

  return (
    <div className="chk-form-section">
      <h2 className="chk-section-heading">3. Emergency Contacts (Up to 3)</h2>
      {navigation}
      <div className='chk-contact-header-row'>
        <h4>Name</h4>
        <h4>Relation</h4>
        <h4>Phone Number</h4>
      </div>
      {data.map((contact, i) => (
        <div key={i} className="chk-contact-row">
          <input
            value={contact.name || ''}
            onChange={(e) => {
            const value = e.target.value;
            handleContactChange(i, 'name', value);
            }}
            placeholder="Name"
            className="chk-input"
          />
          <input
            value={contact.relation || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^[A-Za-z\s]+$/.test(value)) {
                handleContactChange(i, 'relation', value);
              }
            }}
            placeholder="Relation"
            className="chk-input"
          />
          <InputMask
            mask="(XXX) XXX-XXXX"
            replacement={{ X: /\d/ }}
            value={contact.number || ''}
            onChange={(e) => {
              handleContactChange(i, 'number', e.target.value);
            }}
            placeholder="Number"
            className="chk-input"
          />
        </div>
      ))}
      <label className="flex items-center mt-2">
        <input
          type="checkbox"
          checked={hipaaReviewed}
          onChange={(e) => setShowHipaa(e.target.checked)}
          className="mr-2"
          required />
        Review HIPAA and Sign <span className="required">*</span>
      </label>
    </div>
  );
};
// Working here - task - regex
const HipaaForm = ({ data, patientData, setPatientData, close, setHipaaReviewed }) => {
  const [signature, setSignature] = useState(data.hipaa_signature || '');

  const handleSignatureChange = (e) => {
    setSignature(e.target.value);
  };

  const handleSignatureSave = (signatureURL) => {
  const timestamp = new Date().toISOString();
  setPatientData((prev) => ({
    ...prev,
    hipaa_signature_image: signatureURL,
    hipaa_signature_timestamp: timestamp
  }));
};


  const handleSubmit = () => {
    setPatientData((prev) => ({ ...prev, hipaa_signature: signature, hipaaSigned: true }));
    setHipaaReviewed(true);
    close();
  };

   const handleSignatureSubmit = () => {
    if (signature && patientData?.hipaa_signature_image) {
        handleSubmit();
    }
    };

  return (
    <div className="chk-hipaa-form">
      <h2 className="chk-section-heading">Consent for Release of Protected Health Information (PHI)</h2>
      <p className="chk-hipaa-patient-info">
        Patient: <strong>{data.demographics.first_name} {data.demographics.last_name}</strong>, Date Of Birth: <strong>{data.demographics.date_of_birth}</strong>
      </p>
      <h4 className="font-semibold mb-2">I hereby authorize Health Guardian Imaging to disclose the following protected health information (PHI) to Other Healthcare Providers, Insurance, or Emergency Contacts:</h4>
      <ul className="list-disc pl-5 mb-4">
        {data.emergency_contacts.map((c, i) => c.name && <li key={i}>{c.name} ({c.relation})</li>)}
      </ul>
      <h4 className="font-semibold mb-2">Specific Information to be Released:</h4>
      <ul className="list-disc pl-5 mb-4">
        <li>All Medical Records including radiology interpretations</li>
        <li>Lab Results</li>
      </ul>
      <p className="mb-2"><strong>Purpose of Disclosure:</strong> For treatment by another healthcare provider, for insurance purposes, or Emergency contact.</p>
      <h4 className="font-semibold mb-2">Limitations:</h4>
      <ul className="list-disc pl-5 mb-4">
        <li>This authorization is valid for 2 years from the date signed.</li>
        <li>I understand that I can revoke this authorization at any time by providing written notice to Health Guardian Imaging</li>
      </ul>
      <div className="chk-hipaa-signature-area">
        <div className="chk-hipaa-sig-field">
          <label className="chk-hipaa-sig-label" htmlFor="hipaa-sig-input">
            <span className="chk-hipaa-sig-badge">Signature Required</span>
            Type Your Full Name to Sign
          </label>
          <input
            id="hipaa-sig-input"
            type="text"
            value={signature}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^[A-Za-z\s'-]+$/.test(value)) {
                handleSignatureChange(e);
              }
            }}
            placeholder="e.g. Jane A. Smith"
            className="chk-hipaa-sig-input"
          />
          {signature && (
            <span className="chk-hipaa-sig-preview">{signature}</span>
          )}
        </div>
        <NewSignature onSignatureSave={handleSignatureSave} />
        <p className="mb-4">Date: {new Date().toLocaleDateString()}</p>
        <button onClick={handleSignatureSubmit}
          className={`PrimaryButton ${!signature || !patientData?.hipaa_signature_image ? "DisabledButton" : ""}`}
        >Submit</button>
      </div>
    </div>
  );
};

const Review = ({ patientData, navigation }) => {
  const val = (v) => v || <span className="chk-review-empty">Not provided</span>;

  return (
    <div className="chk-form-section chk-review-section">
      <h2 className="chk-section-heading">4. Review Your Information</h2>
      {navigation}
      <p style={{fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px'}}>Please verify all information is correct before submitting.</p>

      {/* Demographics */}
      <h3 className="chk-review-subheading">Demographics</h3>
      <div className="chk-review-card">
        <div className="chk-review-grid">
          <div className="chk-review-field">
            <span className="chk-review-label">First Name</span>
            <span className="chk-review-value">{val(patientData.demographics.first_name)}</span>
          </div>
          <div className="chk-review-field">
            <span className="chk-review-label">Middle Name</span>
            <span className="chk-review-value">{val(patientData.demographics.middle_name)}</span>
          </div>
          <div className="chk-review-field">
            <span className="chk-review-label">Last Name</span>
            <span className="chk-review-value">{val(patientData.demographics.last_name)}</span>
          </div>
          <div className="chk-review-field">
            <span className="chk-review-label">Sex</span>
            <span className="chk-review-value">{val(patientData.demographics.sex)}</span>
          </div>
          <div className="chk-review-field">
            <span className="chk-review-label">Date of Birth</span>
            <span className="chk-review-value">{val(patientData.demographics.date_of_birth)}</span>
          </div>
          <div className="chk-review-field">
            <span className="chk-review-label">SSN</span>
            <span className="chk-review-value">{val(patientData.demographics.ssn)}</span>
          </div>
          <div className="chk-review-field" style={{gridColumn: '1 / -1'}}>
            <span className="chk-review-label">Address</span>
            <span className="chk-review-value">
              {patientData.demographics.street_number} {patientData.demographics.street_name}
              {patientData.demographics.apt_number && `, Apt ${patientData.demographics.apt_number}`}
              {', '}{patientData.demographics.city}, {patientData.demographics.state} {patientData.demographics.zip_code}
            </span>
          </div>
          <div className="chk-review-field">
            <span className="chk-review-label">Primary Phone</span>
            <span className="chk-review-value">{val(patientData.demographics.primary_phone)}</span>
          </div>
          <div className="chk-review-field">
            <span className="chk-review-label">Cell Phone</span>
            <span className="chk-review-value">{val(patientData.demographics.cell_phone)}</span>
          </div>
          <div className="chk-review-field" style={{gridColumn: '1 / -1'}}>
            <span className="chk-review-label">Email</span>
            <span className="chk-review-value">{val(patientData.demographics.email)}</span>
          </div>
          {patientData.demographics.guardian_name && (
            <div className="chk-review-field" style={{gridColumn: '1 / -1'}}>
              <span className="chk-review-label">Parent / Guardian</span>
              <span className="chk-review-value">{patientData.demographics.guardian_name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Billing */}
      <h3 className="chk-review-subheading">Billing Information</h3>
      {patientData.billing.map((insurance, index) => (
        <div key={index} className="chk-review-card" style={{borderLeft: '3px solid #3b82f6'}}>
          <p style={{fontWeight: 600, color: '#1d4ed8', marginBottom: '10px', fontSize: '0.85rem'}}>Insurance {index + 1}</p>
          <div className="chk-review-grid">
            <div className="chk-review-field">
              <span className="chk-review-label">Insurance Name</span>
              <span className="chk-review-value">{val(insurance.insurance_name)}</span>
            </div>
            <div className="chk-review-field">
              <span className="chk-review-label">Phone</span>
              <span className="chk-review-value">{val(insurance.insurance_phone)}</span>
            </div>
            <div className="chk-review-field" style={{gridColumn: '1 / -1'}}>
              <span className="chk-review-label">Address</span>
              <span className="chk-review-value">{val(insurance.insurance_address)}</span>
            </div>
            <div className="chk-review-field">
              <span className="chk-review-label">Member ID</span>
              <span className="chk-review-value">{val(insurance.member_id)}</span>
            </div>
            <div className="chk-review-field">
              <span className="chk-review-label">Group Number</span>
              <span className="chk-review-value">{val(insurance.group_number)}</span>
            </div>
            <div className="chk-review-field">
              <span className="chk-review-label">Effective Date</span>
              <span className="chk-review-value">{val(insurance.effective_date)}</span>
            </div>
            <div className="chk-review-field">
              <span className="chk-review-label">Termination Date</span>
              <span className="chk-review-value">{val(insurance.termination_date)}</span>
            </div>
            <div className="chk-review-field">
              <span className="chk-review-label">Subscriber Name</span>
              <span className="chk-review-value">{val(insurance.subscriber_name)}</span>
            </div>
            <div className="chk-review-field">
              <span className="chk-review-label">Subscriber DOB</span>
              <span className="chk-review-value">{val(insurance.subscriber_dob)}</span>
            </div>
            <div className="chk-review-field">
              <span className="chk-review-label">Relationship</span>
              <span className="chk-review-value">{val(insurance.relationship)}</span>
            </div>
          </div>
        </div>
      ))}

      {/* Emergency Contacts */}
      <h3 className="chk-review-subheading">Emergency Contacts</h3>
      <div className="chk-review-card">
        {patientData.emergency_contacts.filter(c => c.name).length > 0
          ? patientData.emergency_contacts.filter(c => c.name).map((contact, i) => (
              <div key={i} className="chk-review-checked-item" style={{marginBottom: '6px'}}>
                <span style={{fontWeight: 600}}>{contact.name}</span>
                {contact.relation && <span style={{color: '#6b7280'}}>({contact.relation})</span>}
                {contact.number && <span>— {contact.number}</span>}
              </div>
            ))
          : <p className="chk-review-section-empty">No emergency contacts provided</p>
        }
      </div>

      {/* HIPAA */}
      <h3 className="chk-review-subheading">HIPAA Consent</h3>
      <div className="chk-review-card">
        <div className="chk-review-grid">
          <div className="chk-review-field">
            <span className="chk-review-label">Signed</span>
            <span className="chk-review-value">{patientData.hipaaSigned ? 'Yes' : 'No'}</span>
          </div>
          <div className="chk-review-field">
            <span className="chk-review-label">Typed Signature</span>
            <span className="chk-review-value">{val(patientData.hipaa_signature)}</span>
          </div>
        </div>
        {patientData.hipaa_signature_image ? (
          <div style={{marginTop: '12px'}}>
            <span className="chk-review-label">Electronic Signature</span>
            <img src={patientData.hipaa_signature_image} alt="HIPAA Signature" style={{display: 'block', maxWidth: '200px', border: '1px solid #e5e7eb', borderRadius: '6px', marginTop: '6px'}} />
          </div>
        ) : (
          <p className="chk-review-section-empty" style={{marginTop: '8px'}}>Electronic signature not provided</p>
        )}
      </div>
    </div>
  );
};

// Intro gate shown before registration begins. Captures the patient's opt-in to SMS
// appointment reminders (TCPA consent) and the phone number it applies to. Consent is
// optional, but if the patient opts in, the name + phone it applies to are required —
// "Begin Registration" stays disabled until they're provided (an opt-in with no contact
// info is useless). When the box is unchecked, registration proceeds freely.
const SmsConsentGate = ({ smsConsent, setSmsConsent, smsPhone, setSmsPhone, consentName, setConsentName, onBegin }) => {
  const nameComplete = (consentName || '').trim().length > 0;
  const phoneComplete = unformatPhoneNumber(smsPhone || '').length >= 10;
  const consentInfoComplete = nameComplete && phoneComplete;
  const canBegin = !smsConsent || consentInfoComplete;

  return (
    <div className="chk-form-section chk-sms-gate">
      <div className="chk-sms-gate-header">
        <span className="chk-sms-gate-badge">Patient Registration</span>
        <h2 className="chk-sms-gate-title">Welcome to Health Guardian Imaging</h2>
        <p className="chk-sms-gate-subtitle">
          Before we begin, let us know how you'd like to receive appointment reminders.
          This step is optional — you can continue either way.
        </p>
      </div>
      <div className="chk-sms-consent-container">
        <div className="chk-sms-consent-fields">
          <div className="chk-sms-field">
            <label className="chk-sms-field-label" htmlFor="sms-consent-name">
              Full Name{smsConsent && <span style={{ color: '#ef4444' }}> *</span>}
            </label>
            <input
              id="sms-consent-name"
              type="text"
              placeholder="e.g. Jane A. Smith"
              value={consentName}
              onChange={(e) => /^[A-Za-z' -]*$/.test(e.target.value) && setConsentName(e.target.value)}
              className="chk-input chk-sms-consent-input"
              style={smsConsent && !nameComplete ? { borderColor: '#ef4444' } : undefined}
            />
          </div>
          <div className="chk-sms-field">
            <label className="chk-sms-field-label" htmlFor="sms-consent-phone">
              Mobile Phone Number{smsConsent && <span style={{ color: '#ef4444' }}> *</span>}
            </label>
            <input
              id="sms-consent-phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={smsPhone}
              onChange={(e) => setSmsPhone(formatPhoneNumber(e.target.value))}
              className="chk-input chk-sms-consent-input"
              style={smsConsent && !phoneComplete ? { borderColor: '#ef4444' } : undefined}
            />
          </div>
        </div>
        <label className="chk-sms-consent-label">
          <input
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
          />
          <span className="chk-sms-consent-text">
            By checking this box, I consent to receive automated text messages from Health
            Guardian Imaging about my appointments — including reminders, scheduling updates or
            reschedules, and cancellations — at the phone number provided above. Consent is not
            a condition of receiving care or services. Message frequency varies. Message and
            data rates may apply. Reply STOP to unsubscribe or HELP for help. See our{' '}
            <a
              href="https://healthguardianimaging.com/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            {' '}and{' '}
            <a
              href="https://healthguardianimaging.com/privacypolicy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>
      </div>
      <div className="chk-navigation chk-sms-gate-actions">
        {smsConsent && !consentInfoComplete && (
          <p className="chk-sms-consent-required" style={{ color: '#b91c1c', fontSize: 14, margin: '0 0 8px' }}>
            Please enter the full name and a valid mobile phone number to opt in to text messages.
          </p>
        )}
        <button
          onClick={onBegin}
          className="PrimaryButton chk-sms-gate-button"
          disabled={!canBegin}
        >
          Begin Registration
        </button>
      </div>
    </div>
  );
};

const PatientRegistration = ({ appointmentId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {currentUser, setCurrentUser} = useContext(GlobalStateContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const {currentPatient, setCurrentPatient} = useContext(GlobalStateContext);
  const [searchParams] = useSearchParams();
  const risPatientId = searchParams.get('ris_patients_id');
  // Held false until getDemographicInfo's prefill settles, so autosave's first
  // write doesn't capture an empty form and clobber a perfectly good draft.
  const [prefillLoaded, setPrefillLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showHipaa, setShowHipaa] = useState(false);
  const [hipaaReviewed, setHipaaReviewed] = useState(false);
  const [selfPay, setSelfPay] = useState(false);
  // SMS appointment-reminder consent, captured on the intro gate before page 1.
  // registrationStarted gates the wizard until the patient passes the consent screen.
  const [registrationStarted, setRegistrationStarted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsConsentName, setSmsConsentName] = useState('');
  // Persists each insurance block's scanner state (staged images + scan result) above
  // the Billing page so it survives navigating away from / back to the billing section.
  const [cardScans, setCardScans] = useState({}); // { [index]: { images, result } }
  const persistCardScan = useCallback((idx, payload) => {
    setCardScans(prev => ({ ...prev, [idx]: payload }));
  }, []);
  // bi_ids of saved insurance records the user removed; archived (not deleted) on submit.
  const [archivedBillingIds, setArchivedBillingIds] = useState([]);
  const handleArchiveInsurance = useCallback((biId) => {
    setArchivedBillingIds(prev => (prev.includes(biId) ? prev : [...prev, biId]));
  }, []);
  const user = getUserId(currentUser);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [patientExists, setPatientExists] = useState(false) // if the "get" does not find the patient, this stays false
  const [signatures, setSignatures] = useState({
  insured_signature_image: '',
  insured_signature_timestamp: '',
  patient_signature_image: '',
  patient_signature_timestamp: ''
});
  const [patientData, setPatientData] = useState({
    // demographics is the one we change
    demographics: {
    //   medical_record_number: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      date_of_birth: '',
      sex: '',
      street_number: '',
      street_name: '',
      number: '',
      city: '',
      state: '',
      zip_code: '',
      primary_phone: '',
      cell_phone: '',
      email: '',
      ssn: '',
      parent_guardian: '',
      apt_number: ''
    },
    // tempDemographics is the one we keep as the original to compare to
    tempDemographics: {
      first_name: '',
      middle_name: '',
      last_name: '',
      date_of_birth: '',
      sex: '',
      street_number: '',
      street_name: '',
      number: '',
      city: '',
      state: '',
      zip_code: '',
      primary_phone: '',
      cell_phone: '',
      email: '',
      ssn: '',
      parent_guardian: ''
    },
    billing: [
      {
        uid: genInsuranceUid(),
        bi_id: null,
        insurance_name: '',
        insurance_address: '',
        insurance_city: '',
        insurance_state: '',
        insurance_zip: '',
        insurance_phone: '',
        member_id: '',
        group_number: '',
        effective_date: '',
        termination_date: '',
        subscriber_name: '',
        subscriber_dob: '',
        relationship: 'self',
        card_images: []
      }
    ],
    // chief_complaint: '',
    emergency_contacts: Array(3).fill({ c_id: null, name: '', relation: '', number: '' }),
    hipaaSigned: false,
    hipaa_signature: '',
    hipaa_signature_image: null,
    hipaa_signature_timestamp: null,
    patient_signature_image: signatures?.patient_signature_image,
    insured_signature_image: signatures?.insured_signature_image,
    // medical_history: {},
    // surgical_history: {},
    // medications: [{}],
    // allergies: {},
    // social_history: {  
    //   Occupation: { details: '' },
    //   'Marital Status': { details: '' },
    //   Smoking: { checked: false, details: '' },
    //   Alcohol: { checked: false, details: '' },
    // },
    // family_history: {},
    // immunizations: []  // { vaccine: '', dosage: '', notes: '', date: '', ordering_clinician: '' }
  });

  const { stopSaving } = useRegistrationDraft({
    risPatientsId: risPatientId,
    patientData,
    enabled: prefillLoaded,
  });

  // useEffect(() => {
  //   setCurrentUser(null);
  // },[])

  const calculateAge = (dob) => {
    if (!dob) return null;
    const [year, month, day] = dob.split('T')[0].split('-').map(Number);
    const today = new Date();
    let age = today.getFullYear() - year;
    const m = (today.getMonth() + 1) - month;
    if (m < 0 || (m === 0 && today.getDate() < day)) {
      age--;
    }
    return age;
  };

  const isMinor = calculateAge(patientData.demographics?.date_of_birth) < 18;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const requiredFields = {
      'First Name': patientData.demographics.first_name,
      'Last Name': patientData.demographics.last_name,
      'Sex': patientData.demographics.sex,
      'Date of Birth': patientData.demographics.date_of_birth,
      'Street Number': patientData.demographics.street_number,
      'Street Name': patientData.demographics.street_name,
      'City': patientData.demographics.city,
      'State': patientData.demographics.state,
      'Zip Code': patientData.demographics.zip_code,
      'Phone Number': patientData.demographics.primary_phone,
      'Email': patientData.demographics.email

    };
      
    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value || value.trim() === '')
      .map(([field]) => field);

    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields:\n- ${missingFields.join('\n- ')}`);
      setIsSubmitting(false);
      return;
    }

    // Insurance: only enforce the fields payers actually require (CMS-1500 Fields
    // 1/1a/4/6/11a), and only when the patient is using insurance (not self-pay) and
    // has started filling an insurance block. Effective date, group number, termination
    // date and the payer address block are optional. When relationship is "self", the
    // subscriber is the patient, so subscriber name/DOB are derived rather than required.
    if (!selfPay) {
      // "Filled" = the user entered real insurance data. Ignore metadata (uid/bi_id/
      // card_images) and the defaulted relationship, which are present even on an
      // untouched block and would otherwise force validation on an empty block.
      const META_KEYS = new Set(['uid', 'bi_id', 'card_images', 'relationship']);
      const isInsuranceFilled = (ins) => Object.entries(ins)
        .filter(([key]) => !META_KEYS.has(key))
        .some(([, val]) => typeof val === 'string' && val.trim() !== '');

      const insuranceErrors = [];
      (patientData.billing || []).forEach((ins, i) => {
        if (!isInsuranceFilled(ins)) return; // an untouched block is fine
        const required = {
          insurance_name: 'Insurance Name',
          member_id: 'Member ID',
          relationship: 'Relationship to Subscriber',
        };
        if (ins.relationship !== 'self') {
          required.subscriber_name = 'Subscriber Name';
          required.subscriber_dob = 'Subscriber Date of Birth';
        }
        const missing = Object.entries(required)
          .filter(([key]) => !ins[key] || !String(ins[key]).trim())
          .map(([, label]) => label);
        if (missing.length) insuranceErrors.push(`Insurance #${i + 1}: ${missing.join(', ')}`);
      });

      if (insuranceErrors.length > 0) {
        alert(`Please complete the required insurance fields:\n- ${insuranceErrors.join('\n- ')}`);
        setIsSubmitting(false);
        return;
      }
    }

    if (!patientData.hipaaSigned) {
      alert('Please sign the HIPAA form before submitting.');
      setCurrentPage(5);
      setShowHipaa(true);
      return;
    }

      try {
        const finalPatientData = {
          ...patientData,
          ...signatures
        };

        // 🔐 Generate Billing PDF
        // ✅ Use finalPatientData here

        // For "self" relationships the subscriber is the patient; fill the payer-required
        // subscriber identity from demographics so claims/eligibility have a complete record.
        const patientFullName = `${finalPatientData.demographics.first_name || ''} ${finalPatientData.demographics.last_name || ''}`.trim();
        const normalizedBilling = (finalPatientData.billing || []).map((ins) =>
          ins.relationship === 'self'
            ? {
                ...ins,
                subscriber_name: ins.subscriber_name || patientFullName,
                subscriber_dob: ins.subscriber_dob || finalPatientData.demographics.date_of_birth || '',
              }
            : ins
        );

        const postBody = JSON.stringify({
          demographics: finalPatientData.demographics,
          guardian_name: finalPatientData.demographics.parent_guardian,
          license: '',
          selfPay,
          billing: normalizedBilling,
          archivedBillingIds,
          emergency_contacts: finalPatientData.emergency_contacts,
          hipaa_signature: finalPatientData.hipaa_signature,
          hipaa_signature_timestamp: finalPatientData.hipaa_signature_timestamp,
          patient_signature_image: finalPatientData.patient_signature_image,
          patient_signature_timestamp: finalPatientData.patient_signature_timestamp,
          insured_signature_image: finalPatientData.insured_signature_image,
          insured_signature_timestamp: finalPatientData.insured_signature_timestamp,
          hipaaSigned: finalPatientData.hipaaSigned,
          consent_signed: !!finalPatientData.hipaaSigned,
          sms_consent: smsConsent,
          sms_phone: unformatPhoneNumber(smsPhone),
          sms_consent_name: smsConsentName,
          ris_patients_id: risPatientId,
          user,
          type: currentUser?.type,
        });
        const response = await fetch(`${BASE_URL}/patientRegistrationReferred/patientRegistrationReferred`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: postBody
        });
        if (response.status === 201) {
          const data = await response.json();
          await generateBillingPDF({
          patients_id: data.ris_patients_id,
          insuranceData: normalizedBilling,
          patientDemographics: finalPatientData.demographics,
          insuredSignature: {
            url: finalPatientData.insured_signature_image,
            timestamp: finalPatientData.insured_signature_timestamp
          },
          patientSignature: {
            url: finalPatientData.patient_signature_image,
            timestamp: finalPatientData.patient_signature_timestamp
          },
        });

        await generateHipaaPDF({
          patients_id: data.ris_patients_id,
          demographics: finalPatientData.demographics,
          emergencyContacts: finalPatientData.emergency_contacts,
          signatureImage: finalPatientData.hipaa_signature_image,
          signatureText: finalPatientData.hipaa_signature,
          signatureTimestamp: finalPatientData.hipaa_signature_timestamp, 

        });
          // logger.log('Patient Registered');
          alert('Registration completed!');
          // The server already deleted the draft row inside the PATCH that just
          // committed; this just stops a trailing debounced write from racing
          // that and recreating it.
          stopSaving();
          setCurrentUser(null);
          navigate('/login');
        } else {
          try{
            const data = await response?.json();
            // logger.log(data)
            alert(data.error)
          }
          catch(error){
            logger.error("Error: ", error)
          }
        }
      } catch (error) {
        // logger.log(error);
        alert('Error occurred during registration. Please try again.\n\n' + error);
      } finally {
        setIsSubmitting(false);
      }
  };
  
    const demographicsRequired = [
    "first_name", "last_name", "sex", "street_number", "street_name",
    "city", "state", "zip_code", "primary_phone", "email",
    "date_of_birth"
  ];

  const isDemographicsValid = demographicsRequired.every(
    field => patientData.demographics?.[field]?.trim()
  );

  const isHIPAAValid = hipaaReviewed

  // Billing gate: the patient may advance only if (A) Self-Pay is selected, or
  // (B) every insurance block has its payer-required fields AND both the insured
  // and patient billing-release signatures are captured. Required fields mirror the
  // submit-time check (CMS-1500 1/1a/4/11a): insurance name, member ID, relationship,
  // and — when the patient is not the subscriber — subscriber name/DOB. The billing
  // release signatures are only required when not self-pay.
  const isBillingValid = (() => {
    if (selfPay) return true;
    const billing = patientData.billing || [];
    if (billing.length === 0) return false;
    const fieldsValid = billing.every((ins) => {
      if (!ins.insurance_name?.trim()) return false;
      if (!ins.member_id?.trim()) return false;
      if (!ins.relationship?.trim()) return false;
      if (ins.relationship !== 'self') {
        if (!ins.subscriber_name?.trim()) return false;
        if (!ins.subscriber_dob?.trim()) return false;
      }
      return true;
    });
    const signaturesValid = !!signatures.insured_signature_image && !!signatures.patient_signature_image;
    return fieldsValid && signaturesValid;
  })();

  const isValid =
  currentPage === 1 ? isDemographicsValid :
  currentPage === 2 ? isBillingValid :
  // currentPage === 3 ? isChiefComplaintValid :
  currentPage === 3 ? isHIPAAValid :
  true; // default to true for pages with no required fields
 
  const navigation = (
    <div className={`chk-navigation ${currentPage === 1 ? 'chk-navigation-right' : ''}`}>
      {currentPage > 1 && <button onClick={() => setCurrentPage(currentPage - 1)} className="PrimaryButton">Previous</button>}
      {currentPage < 4 && <button onClick={(e) => {
        if (!isValid) {
            e.preventDefault(); // Block action if either group has unacknowledged items
            return;
        }
        setCurrentPage(currentPage + 1)}} 
        className={`PrimaryButton ${!isValid ? "DisabledButton" : ""}`}>Next</button>}
      {currentPage === 4 && <button onClick={handleSubmit} className="PrimaryButton" disabled={isSubmitting}>{isSubmitting ? "Submitting..." : "Submit"}</button>} 
    </div>
  );

  const stringifyValues = obj => {
    if (Array.isArray(obj)) {
      return obj.map(item => stringifyValues(item));
    } else if (obj !== null && typeof obj === 'object') {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, stringifyValues(value)])
      );
    } else {
      return obj === null ? '' : String(obj); // ✅ convert null to empty string
    }
  }

  const getDemographicInfo = async () => {
    try {
      const response = await fetch(`${BASE_URL}/patientRegistrationReferred/displayDemographics?ris_patients_id=${risPatientId}`);
      if (response.ok) {
        const data = await response.json();
        // logger.log(data)

        // A returning patient who has already opted in to SMS shouldn't be shown the
        // consent gate again. Reflect their existing opt-in (so it's preserved on
        // resubmit rather than being reset to declined) and skip into the wizard.
        if (Number(data?.sms_consent) === 1) {
          setSmsConsent(true);
          setRegistrationStarted(true);
        }

        const stringifiedData = stringifyValues(data);

        // logger.log(stringifiedData)
        if (stringifiedData?.billing.length === 0) {
          stringifiedData.billing = [
            {
              uid: genInsuranceUid(),
              bi_id: null,
              insurance_name: '',
              insurance_address: '',
              insurance_city: '',
              insurance_state: '',
              insurance_zip: '',
              insurance_phone: '',
              member_id: '',
              group_number: '',
              effective_date: '',
              termination_date: '',
              subscriber_name: '',
              subscriber_dob: '',
              relationship: 'self',
              card_images: []
            }
          ]
        }

        if (stringifiedData.contacts.length !== 3) {
            const size = 3 - stringifiedData.contacts.length;
            const emptyContacts = Array(size).fill({ c_id: null, name: '', relation: '', number: '' });

            // Append empty contacts to the existing ones
            stringifiedData.contacts = [...stringifiedData.contacts, ...emptyContacts];
        }
      
        if (stringifiedData) {
          const demo = stringifiedData?.demographics;
          if (demo) {
            if (demo.primary_phone) demo.primary_phone = formatPhoneNumber(demo.primary_phone);
            if (demo.cell_phone) demo.cell_phone = formatPhoneNumber(demo.cell_phone);
            // Pre-fill the SMS consent phone (intro gate) from the patient's number,
            // preferring cell over primary, so they don't re-type it.
            const smsDefault = demo.cell_phone || demo.primary_phone;
            if (smsDefault) setSmsPhone(prev => prev || smsDefault);
            const nameDefault = `${demo.first_name || ''} ${demo.last_name || ''}`.trim();
            if (nameDefault) setSmsConsentName(prev => prev || nameDefault);
          }

          const billing = stringifiedData?.billing?.map(ins => ({
            ...ins,
            uid: ins.uid || genInsuranceUid(),
            insurance_phone: ins.insurance_phone ? formatPhoneNumber(ins.insurance_phone) : ins.insurance_phone,
          }));

          const contacts = stringifiedData?.contacts?.map(c => ({
            ...c,
            number: c.number ? formatPhoneNumber(c.number) : c.number,
          }));

          setPatientData(prev => ({
            ...prev,
            demographics: demo,
            tempDemographics: demo,
            billing: billing,
            emergency_contacts: contacts,
          }));
        }
        setPatientExists(true)
      } else {
        setPatientExists(false)
        // logger.log("Error getting patient info");
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setPrefillLoaded(true);
    }
  };

  // Taken only when the office assistant clicked "Resume Registration" from
  // the Location modal (state.restoreDraft) against this named patient —
  // never restored automatically, since the kiosk is a shared device. Runs
  // after getDemographicInfo's prefill so the draft's more recent answers win.
  const restoreDraftIfRequested = async () => {
    if (!location.state?.restoreDraft || !risPatientId) return;
    try {
      const response = await fetch(`${BASE_URL}/patientRegistrationReferred/draft?ris_patients_id=${risPatientId}`);
      if (!response.ok) return;
      const data = await response.json();
      if (!data?.draft?.payload) return;
      setPatientData(prev => ({ ...prev, ...stripSignatures(data.draft.payload) }));
    } catch (error) {
      logger.error('Error restoring registration draft:', error);
    }
  };

  useEffect(() => {
    (async () => {
      await getDemographicInfo();
      await restoreDraftIfRequested();
    })();
    // logger.log(patientData.demographics)
  }, [])

  return (
    <div>
    {/* <NavBar /> */}
    <div className="chk-app-container">
      {risPatientId && ( 
        <div className="chk-mrn-display">
          Patient ID: {risPatientId}
        </div>
      )}
      {!registrationStarted && (
        <SmsConsentGate
          smsConsent={smsConsent}
          setSmsConsent={setSmsConsent}
          smsPhone={smsPhone}
          setSmsPhone={setSmsPhone}
          consentName={smsConsentName}
          setConsentName={setSmsConsentName}
          onBegin={() => setRegistrationStarted(true)}
        />
      )}
      {registrationStarted && currentPage === 1 && (
        <Demographics
          data={patientData.demographics}
          setData={(d) => setPatientData({ ...patientData, demographics: d })}
          navigation={navigation}
          isMinor={isMinor}
        />
      )}
      {currentPage === 2 && (
        <Billing
          data={patientData.billing}
          setData={(updater) => setPatientData(prev => ({ ...prev, billing: typeof updater === 'function' ? updater(prev.billing) : updater }))}
          signatures={signatures}
          setSignatures={setSignatures}
          navigation={navigation}
          demographics={patientData.demographics}
          selfPay={selfPay}
          setSelfPay={setSelfPay}
          risPatientId={risPatientId}
          cardScans={cardScans}
          persistCardScan={persistCardScan}
          onArchiveInsurance={handleArchiveInsurance}
        />
      )}
      {/* {currentPage === 3 && (
        <ChiefComplaint
          data={patientData.chief_complaint}
          setData={(d) => setPatientData({ ...patientData, chief_complaint: d })}
          navigation={navigation}
        />
      )} */}
      {currentPage === 3 && !showHipaa && (
        <EmergencyContact
          data={patientData.emergency_contacts}
          setData={(d) => setPatientData({ ...patientData, emergency_contacts: d })}
          setShowHipaa={setShowHipaa}
          hipaaReviewed={hipaaReviewed}
          setHipaaReviewed={setHipaaReviewed}
          navigation={navigation}
        />
      )}
      {currentPage === 3 && showHipaa && (
        <HipaaForm
          data={patientData}
          patientData={patientData}
          setPatientData={setPatientData}
          close={() => setShowHipaa(false)}
          setHipaaReviewed={setHipaaReviewed}
        />
      )}
      
      {currentPage === 4 && <Review patientData={patientData} navigation={navigation} />}
    </div>
    </div>
  );
};

export default PatientRegistration;

